/**
 * FIELD-TEST BUG 3 — carry an invite code across the Google OAuth round-trip.
 *
 * An athlete opens `tayro.run/join/<code>` in Safari signed OUT. The server
 * lands them on `/v2/landing?join=<code>`, they tap "Continue with Google" —
 * and the code is gone. `signInWithGoogle` sets `redirectTo` to
 * `${origin}/v2/today` (an ABSOLUTE, Supabase-allow-listed URL that we must
 * not vary per-visitor), so the OAuth callback returns to /v2/today carrying
 * nothing about the invite. The athlete arrives signed in, on their own empty
 * Today, having never joined. That was the dead end the founder hit.
 *
 * The stash bridges that gap: /v2/landing writes the code before sign-in,
 * <JoinCodeMount> reads it once after the session appears and forwards to
 * `/v2/team?join=<code>`, where the existing pre-filled <JoinTeamModal> asks
 * for the one explicit confirmation the flow has always had. No new screen.
 *
 * sessionStorage, deliberately:
 *   - it survives the cross-origin OAuth navigation (same tab, same origin
 *     on return) — which is the whole requirement;
 *   - in the Capacitor shell the WebView never navigates at all (the system
 *     browser owns OAuth and returns via `tayro://auth-callback`), so it
 *     survives there too;
 *   - and it dies with the tab, so an invite the athlete abandoned cannot
 *     resurface weeks later and pre-fill a join they no longer expect.
 *
 * Every access is try/caught: Safari throws on storage in some private /
 * blocked-cookie configurations, and a failed stash must degrade to today's
 * behaviour (athlete pastes the link into the join modal), never to a crash.
 */

const KEY = 'tayro.pendingJoinCode.v1';

/** Codes are the server's own alphabet (see `generateInviteCode`); anything
 *  else is not ours and never reaches the API. */
const CODE_RE = /^[A-Za-z0-9_-]{1,64}$/;

export function stashJoinCode(code: string | null | undefined): void {
  const clean = (code ?? '').trim();
  if (!clean || !CODE_RE.test(clean)) return;
  try {
    window.sessionStorage.setItem(KEY, clean);
  } catch {
    /* storage blocked — the athlete can still paste the link manually */
  }
}

/** Read AND clear. One-shot by construction: a consumed code can never
 *  re-fire on a later navigation. */
export function takeStashedJoinCode(): string | null {
  let raw: string | null = null;
  try {
    raw = window.sessionStorage.getItem(KEY);
    window.sessionStorage.removeItem(KEY);
  } catch {
    return null;
  }
  const clean = (raw ?? '').trim();
  if (!clean || !CODE_RE.test(clean)) return null;
  return clean;
}

/** Exported for tests + the mount's cheap "is there anything to do" check. */
export const JOIN_STASH_KEY = KEY;
